import React from 'react';
import { cn } from '../../ui/cn';

type IconProps = {
  className?: string;
};

const GmailIcon = ({ className = 'h-4 w-4' }: IconProps) => (
  <svg viewBox="52 42 88 66" className={className} aria-hidden="true">
    <path fill="#4285f4" d="M58 108h14V74L52 59v43c0 3.32 2.69 6 6 6" />
    <path fill="#34a853" d="M120 108h14c3.32 0 6-2.69 6-6V59l-20 15" />
    <path fill="#fbbc04" d="M120 48v26l20-15v-8c0-7.42-8.47-11.65-14.4-7.2" />
    <path fill="#ea4335" d="M72 74V48l24 18 24-18v26L96 92" />
    <path fill="#c5221f" d="M52 51v8l20 15V48l-5.6-4.2c-5.94-4.45-14.4-.22-14.4 7.2" />
  </svg>
);

const IOSIcon = ({ className = 'h-4 w-4' }: IconProps) => {
  const gradientId = React.useId();

  return (
    <svg viewBox="0 0 602 602" className={className} aria-hidden="true">
      <defs>
        <linearGradient id={gradientId} x1="305.201" y1="598.591" x2="305.785" y2="8.24341" gradientUnits="userSpaceOnUse">
          <stop stopColor="#70EFFF" />
          <stop offset="1" stopColor="#5770FF" />
        </linearGradient>
      </defs>
      <path
        d="M138.684 1H463.316C539.593 1 601 62.4069 601 138.684V463.316C601 539.593 539.593 601 463.316 601H138.684C62.4071 601 1 539.593 1 463.316V138.684C1 62.4069 62.4071 1 138.684 1Z"
        fill={`url(#${gradientId})`}
      />
      <path
        d="M132.458 171.625C129.297 171.625 126.319 172.17 123.5 173.292L179.958 231.417L237.042 290.583L238.083 291.833L239.75 293.5L241.417 295.167L244.75 298.708L293.708 348.917C294.523 349.423 296.886 351.611 298.731 352.534C301.108 353.722 303.685 354.817 306.341 354.912C309.207 355.015 312.137 354.194 314.716 352.939C316.647 352 317.506 350.653 319.75 348.917L376.417 290.375L433.708 231.417L488.917 174.542C485.372 172.621 481.447 171.625 477.25 171.625H132.458ZM115.167 178.708C109.146 184.413 105.375 192.989 105.375 202.667V393.5C105.375 401.336 107.892 408.454 112.042 413.917L119.958 406.417L178.917 349.125L231.208 298.5L230.167 297.25L172.875 238.292L115.583 179.125L115.167 178.708ZM496.417 180.583L440.583 238.292L383.5 297.25L382.458 298.292L436.833 351L495.792 408.292L499.333 411.625C502.508 406.532 504.333 400.257 504.333 393.5V202.667C504.333 194.038 501.343 186.214 496.417 180.583ZM237.875 305.375L185.792 356L126.625 413.292L119.125 420.583C123.079 423.13 127.579 424.75 132.458 424.75H477.25C483.116 424.75 488.437 422.493 492.875 418.917L489.125 415.167L429.958 357.875L375.583 305.375L326.625 355.792C323.975 357.548 322.205 359.497 319.617 360.693C315.453 362.619 310.889 364.248 306.302 364.177C301.703 364.107 297.193 362.307 293.065 360.277C290.993 359.257 289.889 358.244 287.458 356.208L237.875 305.375Z"
        fill="#fff"
      />
    </svg>
  );
};

const AppleIcon = ({ className }: IconProps) => (
  <svg
    viewBox="0 0 814 1000"
    className={cn('h-4 w-4 fill-current text-neutral-900', className)}
    aria-hidden="true">
    <path d="M788.1 340.9c-5.8 4.5-108.2 62.2-108.2 190.5 0 148.4 130.3 200.9 134.2 202.2-.6 3.2-20.7 71.9-68.7 141.9-42.8 61.6-87.5 123.1-155.5 123.1s-85.5-39.5-164-39.5c-76.5 0-103.7 40.8-165.9 40.8s-105.6-57-155.5-127C46.7 790.7 0 663 0 541.8c0-194.4 126.4-297.5 250.8-297.5 66.1 0 121.2 43.4 162.7 43.4 39.5 0 101.1-46 176.3-46 28.5 0 130.9 2.6 198.3 99.2zm-234-181.5c31.1-36.9 53.1-88.1 53.1-139.3 0-7.1-.6-14.3-1.9-20.1-50.6 1.9-110.8 33.7-147.1 75.8-28.5 32.4-55.1 83.6-55.1 135.5 0 7.8 1.3 15.6 1.9 18.1 3.2.6 8.4 1.3 13.6 1.3 45.4 0 102.5-30.4 135.5-71.3z" />
  </svg>
);

const OutlookIcon = ({ className = 'h-4 w-4' }: IconProps) => {
  const prefix = React.useId();
  const clipId = `${prefix}-clip`;
  const paint0 = `${prefix}-paint0`;
  const paint1 = `${prefix}-paint1`;
  const paint2 = `${prefix}-paint2`;
  const paint3 = `${prefix}-paint3`;
  const paint4 = `${prefix}-paint4`;
  const paint5 = `${prefix}-paint5`;
  const paint6 = `${prefix}-paint6`;
  const paint7 = `${prefix}-paint7`;
  const paint8 = `${prefix}-paint8`;
  const paint9 = `${prefix}-paint9`;
  const paint10 = `${prefix}-paint10`;

  return (
    <svg viewBox="0 0 541 540" className={className} aria-hidden="true">
      <g clipPath={`url(#${clipId})`}>
        <path d="M374.984 49.7446L30.6367 268.014L1.02344 221.295V181.038C1.02344 166.381 8.44531 152.721 20.7422 144.745L220.91 14.8579C251.406 -4.93115 290.688 -4.93506 321.188 14.8501L374.984 49.7446Z" fill={`url(#${paint0})`} />
        <path d="M318.102 12.9395C319.137 13.5527 320.164 14.1934 321.184 14.8535L477.398 116.186L90.0625 361.705L30.625 267.936L314.895 87.4004C341.82 70.2988 343 31.8301 318.102 12.9395Z" fill={`url(#${paint1})`} />
        <path d="M318.102 12.9395C319.137 13.5527 320.164 14.1934 321.184 14.8535L477.398 116.186L90.0625 361.705L30.625 267.936L314.895 87.4004C341.82 70.2988 343 31.8301 318.102 12.9395Z" fill={`url(#${paint2})`} />
        <path d="M244.602 408.588L90.0664 361.709L418.629 153.436C446.301 135.896 446.23 95.498 418.496 78.0566L417.016 77.127L421.277 79.7754L521.273 144.643C533.574 152.619 540.996 166.283 540.996 180.943V219.904L244.602 408.588Z" fill={`url(#${paint3})`} />
        <path d="M244.602 408.588L90.0664 361.709L418.629 153.436C446.301 135.896 446.23 95.498 418.496 78.0566L417.016 77.127L421.277 79.7754L521.273 144.643C533.574 152.619 540.996 166.283 540.996 180.943V219.904L244.602 408.588Z" fill={`url(#${paint4})`} />
        <path d="M321.188 14.8501C290.688 -4.93506 251.406 -4.93115 220.906 14.8579L20.7422 144.745C8.44531 152.721 1.02344 166.381 1.02344 181.038V183.006C1.50781 197.721 9.25 211.28 21.7578 219.167L270.645 376.077L520.16 219.405C533.121 211.268 540.984 197.042 540.984 181.741V219.909L540.992 180.944C540.992 166.284 533.566 152.624 521.27 144.643L321.188 14.8501Z" fill={`url(#${paint5})`} />
        <path d="M226.77 539.65H447.219C498.996 539.65 540.969 497.678 540.969 445.9V181.74C540.969 197.041 533.105 211.268 520.148 219.404L192.242 425.295C174.555 436.404 163.82 455.822 163.82 476.709C163.824 511.471 192.004 539.65 226.77 539.65Z" fill={`url(#${paint6})`} />
        <path d="M226.77 539.65H447.219C498.996 539.65 540.969 497.678 540.969 445.9V181.74C540.969 197.041 533.105 211.268 520.148 219.404L192.242 425.295C174.555 436.404 163.82 455.822 163.82 476.709C163.824 511.471 192.004 539.65 226.77 539.65Z" fill={`url(#${paint7})`} />
        <path d="M226.77 539.65H447.219C498.996 539.65 540.969 497.678 540.969 445.9V181.74C540.969 197.041 533.105 211.268 520.148 219.404L192.242 425.295C174.555 436.404 163.82 455.822 163.82 476.709C163.824 511.471 192.004 539.65 226.77 539.65Z" fill={`url(#${paint8})`} />
        <path d="M316.402 539.635H94.7383C42.9609 539.635 0.988281 497.662 0.988281 445.885V181.545C0.988281 196.818 8.82422 211.021 21.7422 219.166L349.324 425.686C367.258 436.99 378.133 456.713 378.133 477.912C378.129 512.002 350.492 539.635 316.402 539.635Z" fill={`url(#${paint9})`} />
        <path d="M316.402 539.635H94.7383C42.9609 539.635 0.988281 497.662 0.988281 445.885V181.545C0.988281 196.818 8.82422 211.021 21.7422 219.166L349.324 425.686C367.258 436.99 378.133 456.713 378.133 477.912C378.129 512.002 350.492 539.635 316.402 539.635Z" fill={`url(#${paint10})`} />
      </g>
      <defs>
        <linearGradient id={paint0} x1="60.8362" y1="245.074" x2="374.983" y2="50.2243" gradientUnits="userSpaceOnUse">
          <stop stopColor="#20A7FA" />
          <stop offset="0.4" stopColor="#3BD5FF" />
          <stop offset="1" stopColor="#C4B0FF" />
        </linearGradient>
        <linearGradient id={paint1} x1="168.958" y1="311.517" x2="343.843" y2="31.4859" gradientUnits="userSpaceOnUse">
          <stop stopColor="#165AD9" />
          <stop offset="0.5008" stopColor="#1880E5" />
          <stop offset="1" stopColor="#8587FF" />
        </linearGradient>
        <linearGradient id={paint2} x1="296.507" y1="315.326" x2="102.344" y2="157.119" gradientUnits="userSpaceOnUse">
          <stop offset="0.236946" stopColor="#448AFF" stopOpacity="0" />
          <stop offset="0.792113" stopColor="#0032B1" stopOpacity="0.2" />
        </linearGradient>
        <linearGradient id={paint3} x1="271.801" y1="376.248" x2="578.65" y2="179.865" gradientUnits="userSpaceOnUse">
          <stop stopColor="#1A43A6" />
          <stop offset="0.492267" stopColor="#2052CB" />
          <stop offset="1" stopColor="#5F20CB" />
        </linearGradient>
        <linearGradient id={paint4} x1="358.421" y1="364.509" x2="171.962" y2="203.162" gradientUnits="userSpaceOnUse">
          <stop stopColor="#0045B9" stopOpacity="0" />
          <stop offset="0.669859" stopColor="#0D1F69" stopOpacity="0.2" />
        </linearGradient>
        <radialGradient id={paint5} cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(271.027 11.8683) rotate(-90) scale(405.041 438.393)">
          <stop offset="0.568182" stopColor="#275FF0" stopOpacity="0" />
          <stop offset="0.992424" stopColor="#002177" />
        </radialGradient>
        <linearGradient id={paint6} x1="540.97" y1="358.746" x2="268.775" y2="358.746" gradientUnits="userSpaceOnUse">
          <stop stopColor="#4DC4FF" />
          <stop offset="0.196145" stopColor="#0FAFFF" />
        </linearGradient>
        <radialGradient id={paint7} cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(332.392 478.275) rotate(-45) scale(173.58)">
          <stop offset="0.259477" stopColor="#0060D1" stopOpacity="0.4" />
          <stop offset="0.908166" stopColor="#0383F1" stopOpacity="0" />
        </radialGradient>
        <radialGradient id={paint8} cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(70.471 606.68) rotate(-52.6577) scale(589.221 532.806)">
          <stop offset="0.732317" stopColor="#F4A7F7" stopOpacity="0" />
          <stop offset="1" stopColor="#F4A7F7" stopOpacity="0.501961" />
        </radialGradient>
        <radialGradient id={paint9} cx="0" cy="0" r="1" gradientUnits="userSpaceOnUse" gradientTransform="translate(189.562 322.578) rotate(123.339) scale(310.887 806.789)">
          <stop stopColor="#49DEFF" />
          <stop offset="0.724349" stopColor="#29C3FF" />
        </radialGradient>
        <linearGradient id={paint10} x1="-37.1366" y1="477.684" x2="224.936" y2="477.495" gradientUnits="userSpaceOnUse">
          <stop offset="0.205882" stopColor="#6CE0FF" />
          <stop offset="0.535" stopColor="#50D5FF" stopOpacity="0" />
        </linearGradient>
        <clipPath id={clipId}>
          <rect width="541" height="540" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

const YahooIcon = ({ className = 'h-4 w-4' }: IconProps) => {
  const clipId = React.useId();

  return (
    <svg viewBox="0 0 512 512" className={className} aria-hidden="true">
      <g clipPath={`url(#${clipId})`}>
        <path fillRule="evenodd" clipRule="evenodd" d="M105 0H407C464.927 0.154 511.846 47.073 512 104.995V407C511.846 464.926 464.927 511.844 407.005 511.998L105 512C47.074 511.844 0.156 464.926 0.002 407.003L0 105C0.156 47.073 47.074 0.154 104.997 0H105Z" fill="#5F01D1" />
        <path d="M106.598 187.847H163.586L196.77 272.742L230.386 187.847H285.867L202.322 388.822H146.48L169.351 335.567L106.6 187.847H106.598ZM350.477 255.68H288.333L343.489 123.178L405.406 123.181L350.477 255.68ZM304.612 268.362C323.679 268.362 339.135 283.819 339.135 302.883C339.135 321.948 323.679 337.405 304.612 337.405C285.547 337.405 270.093 321.948 270.093 302.883C270.093 283.819 285.549 268.362 304.612 268.362Z" fill="white" />
      </g>
      <defs>
        <clipPath id={clipId}>
          <rect width="512" height="512" fill="white" />
        </clipPath>
      </defs>
    </svg>
  );
};

export function PreviewClientIcon({
  icon,
  className,
}: {
  icon?: string;
  className?: string;
}) {
  if (icon === 'gmail') return <GmailIcon className={className} />;
  if (icon === 'ios') return <IOSIcon className={className} />;
  if (icon === 'apple') return <AppleIcon className={className} />;
  if (icon === 'outlook') return <OutlookIcon className={className} />;
  if (icon === 'yahoo') return <YahooIcon className={className} />;
  return null;
}
